/**
 * The reference-letter request form's proxy routes (proxy-server/lib/referenceLetterForm.js). Open
 * to every signed-in colleague; whom they may file for is decided by the proxy on each request.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { EarlierRequest, FormContext, FormSubmission, GradeWording, WordingQuery } from '../types/referenceLetterForm';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

/** A refused submission, with the per-field problems the proxy found (a 400). */
export class FormSubmissionError extends Error {
  details: Record<string, string>;

  constructor(message: string, details: Record<string, string> = {}) {
    super(message);
    this.details = details;
  }
}

/** Who is asking, whom they may request a letter for, and the choices. */
export async function fetchFormContext(): Promise<FormContext> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/reference-letter-form/context`);
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error || `The form could not be loaded (${res.status})`);
  }
  return res.json();
}

/**
 * The paragraph each grade puts in the letter for these answers. `null` when the proxy will not say
 * (400: the answers are not complete; 403: the person is out of reach) — the form then shows no
 * wording, which is not an error to report. A server failure throws and the form says so in place:
 * the wording is a help, so no status here raises the app's global error.
 */
export async function fetchGradeWording(query: WordingQuery): Promise<GradeWording | null> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/reference-letter-form/wording`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(query),
    suppressErrorStatuses: [400, 403, 500, 502, 503, 504],
  });
  if (res.status === 400 || res.status === 403) return null;
  if (!res.ok) throw new Error(`The wording could not be loaded (${res.status})`);
  return res.json();
}

/**
 * The earlier request of this type for this person, which the form prefills; `null` when there is
 * none, or when the proxy will not say (400, 403). A server failure throws, and the page says it
 * could not check — Send is then refused by the proxy if a request it did not show exists.
 */
export async function fetchEarlierRequest(query: { employeeId: string; type: string }): Promise<EarlierRequest | null> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/reference-letter-form/earlier?${new URLSearchParams(query)}`, {
    suppressErrorStatuses: [400, 403, 500, 502, 503, 504],
  });
  if (res.status === 400 || res.status === 403) return null;
  if (!res.ok) throw new Error(`Earlier requests could not be checked (${res.status})`);
  return (await res.json()).request ?? null;
}

// Answered with words for the page: the per-field problems (400), outside your reach (403), a
// request sent a few minutes ago (409). Without these, authenticatedFetch throws before the body is
// read and reports a global error the page then shows a second time.
const FORM_ANSWERS = [400, 403, 409];

/**
 * Files the request in Airtable, or updates the earlier one named in `updates`; the proxy checks the
 * person is in the caller's reach and that `updates` is still the request it would update.
 */
export async function submitReferenceLetterRequest(body: FormSubmission): Promise<{ id: string; updated: boolean }> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/reference-letter-form/requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    suppressErrorStatuses: FORM_ANSWERS,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new FormSubmissionError(data?.error || `The request could not be sent (${res.status})`, data?.details);
  return data;
}
